import { useEffect, useState } from 'react';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';
import useAuth from '../hooks/useAuth';

export default function StudentPromotionPage() {
  const { currentBranch, user } = useAuth();

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [filterClasses, setFilterClasses] = useState([]);
  const [promotionType, setPromotionType] = useState('december_annual');
  const [academicYear, setAcademicYear] = useState(() => String(new Date().getFullYear()));
  const [executionMode, setExecutionMode] = useState('scheduled');
  const [effectiveDate, setEffectiveDate] = useState(() => `${Number(new Date().getFullYear()) + 1}-01-01`);

  const [students, setStudents] = useState([]);
  const [promotionClasses, setPromotionClasses] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [targetClassMap, setTargetClassMap] = useState({});

  const [loading, setLoading] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Dynamic Academic Years generator
  const getDynamicYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear - 1; y <= currentYear + 4; y++) {
      years.push(String(y));
    }
    return years;
  };

  // Helper: Format Age in Years & Months
  const formatAgeDisplay = (totalMonths) => {
    if (!totalMonths || totalMonths <= 0) return '0 mos';
    const yrs = Math.floor(totalMonths / 12);
    const mos = totalMonths % 12;
    if (yrs === 0) return `${mos} mos`;
    if (mos === 0) return `${yrs} Yrs (${totalMonths} mos)`;
    return `${yrs} Yrs ${mos} Mos (${totalMonths} mos)`;
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      loadBranches();
    }
  }, [user]);

  useEffect(() => {
    const activeBranchId = user?.role === 'admin' 
      ? selectedBranch 
      : (currentBranch?.branch_id || user?.branch_id || selectedBranch);

    if (activeBranchId) {
      loadBranchClasses(activeBranchId);
    } else {
      setFilterClasses([]);
      setSelectedClass('');
    }
  }, [selectedBranch, user, currentBranch]);

  async function loadBranchClasses(bId) {
    try {
      const res = await api.get(`/branches/${bId}/classes`);
      setFilterClasses(res.data.data || []);
    } catch (err) {
      setFilterClasses([]);
    }
  }

  useEffect(() => {
    if (promotionType === 'june_mid_year') {
      setExecutionMode('immediate');
      setEffectiveDate(new Date().toISOString().split('T')[0]);
    } else {
      setExecutionMode('scheduled');
      const nextYr = Number(academicYear) || new Date().getFullYear();
      setEffectiveDate(`${nextYr + 1}-01-01`);
    }
  }, [promotionType, academicYear]);

  useEffect(() => {
    fetchPromotionData();
  }, [selectedBranch, selectedClass, promotionType, academicYear, user, currentBranch]);

  async function loadBranches() {
    try {
      const res = await api.get('/admin/branches');
      setBranches(res.data.data || []);
    } catch (err) {}
  }

  async function fetchPromotionData() {
    setLoading(true);
    setError('');
    try {
      const activeBranchId = user?.role === 'admin' 
        ? selectedBranch 
        : (currentBranch?.branch_id || user?.branch_id || selectedBranch);

      const params = { promotion_type: promotionType };
      if (activeBranchId) params.branch_id = activeBranchId;
      if (selectedClass) params.class_id = selectedClass;

      const res = await api.get('/students/promotion/eligibility', { params });
      const studentsList = res.data.data || [];
      const classesList = res.data.classes || [];

      setStudents(studentsList);
      setPromotionClasses(classesList);

      const initialTargets = {};
      const initialSelected = [];
      studentsList.forEach(s => {
        if (s.recommended_target_class_id) {
          initialTargets[s.id] = String(s.recommended_target_class_id);
        }
        const isPromotedThisYear = s.last_completed_promotion && String(s.last_completed_promotion.academic_year) === String(academicYear);
        const isScheduled = Boolean(s.scheduled_promotion);
        
        // Auto-select only if eligible AND not already scheduled AND not already promoted for this academic year
        if (s.is_eligible && !isScheduled && !isPromotedThisYear) {
          initialSelected.push(s.id);
        }
      });
      setTargetClassMap(initialTargets);
      setSelectedStudentIds(initialSelected);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load promotion data');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedStudentIds(students.filter(s => {
        const isPromotedThisYear = s.last_completed_promotion && String(s.last_completed_promotion.academic_year) === String(academicYear);
        return s.is_eligible && !s.scheduled_promotion && !isPromotedThisYear;
      }).map(s => s.id));
    } else {
      setSelectedStudentIds([]);
    }
  };

  const toggleStudentSelect = (id) => {
    setSelectedStudentIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handlePromoteSelected = async () => {
    if (selectedStudentIds.length === 0) return;
    setIsPromoting(true);
    setError('');
    setSuccessMessage('');
    try {
      const promotionsPayload = selectedStudentIds.map(stId => ({
        student_id: stId,
        to_class_id: targetClassMap[stId]
      })).filter(item => item.to_class_id);

      if (promotionsPayload.length === 0) {
        setError('Please select a target class for all selected students.');
        setIsPromoting(false);
        return;
      }

      const res = await api.post('/students/promotion/promote', {
        promotions: promotionsPayload,
        promotion_type: promotionType,
        academic_year: academicYear,
        execution_mode: executionMode,
        effective_date: executionMode === 'scheduled' ? effectiveDate : null
      });

      setSuccessMessage(res.data.message || 'Students promoted successfully!');
      fetchPromotionData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to promote students');
    } finally {
      setIsPromoting(false);
    }
  };

  const handleCancelScheduledPromotion = async (promotionId) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled promotion?')) return;
    setError('');
    setSuccessMessage('');
    try {
      await api.delete(`/students/promotion/scheduled/${promotionId}`);
      setSuccessMessage('Scheduled promotion cancelled successfully!');
      fetchPromotionData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel scheduled promotion');
    }
  };

  const scheduledStudents = students.filter(s => s.scheduled_promotion);

  return (
    <div style={{ paddingBottom: 40 }}>
      <PageHeader 
        title="🎓 Student Class Promotion" 
        subtitle="Manage annual session rollovers and June mid-year age-based promotions" 
      />

      {successMessage && <div className="success-message" style={{ marginBottom: 16 }}>{successMessage}</div>}
      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Academic Terms Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
        border: '1px solid #c7d2fe', borderRadius: 12, padding: '16px 20px', marginBottom: 20,
        boxShadow: '0 2px 6px rgba(99, 102, 241, 0.08)'
      }}>
        <div style={{ fontWeight: 700, color: '#3730a3', fontSize: 15, marginBottom: 6 }}>
          📅 Academic Terms & Promotion Rules Overview
        </div>
        <div style={{ fontSize: 13, color: '#4338ca', display: 'flex', gap: 24, flexWrap: 'wrap', lineHeight: 1.5 }}>
          <span><strong>Term 1:</strong> Jan – Apr</span>
          <span><strong>Term 2 (Mid-Year):</strong> June – Aug *(26+ mos / 3+ yrs promotion)*</span>
          <span><strong>Term 3 (Year-End):</strong> Sept – Dec *(Annual December promotion for Jan session)*</span>
        </div>
      </div>

      {/* Filter & Configuration Bar */}
      <div className="card" style={{ padding: 18, marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          {user?.role === 'admin' && (
            <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', minWidth: 160 }}>
              Branch
              <select
                value={selectedBranch}
                onChange={(e) => {
                  setSelectedBranch(e.target.value);
                  setSelectedClass('');
                }}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', marginTop: 4, fontSize: 14 }}
              >
                <option value="">All Branches</option>
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </label>
          )}

          <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', minWidth: 160 }}>
            Class Filter
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              disabled={user?.role === 'admin' && !selectedBranch}
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', marginTop: 4, fontSize: 14,
                backgroundColor: (user?.role === 'admin' && !selectedBranch) ? '#f1f5f9' : '#ffffff',
                cursor: (user?.role === 'admin' && !selectedBranch) ? 'not-allowed' : 'pointer'
              }}
            >
              <option value="">
                {user?.role === 'admin' && !selectedBranch ? 'Select Branch First' : 'All Classes'}
              </option>
              {filterClasses.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.section ? ` (${c.section})` : ''}
                </option>
              ))}
            </select>
          </label>

          <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', flex: 1, minWidth: 220 }}>
            Promotion Type
            <select
              value={promotionType}
              onChange={(e) => setPromotionType(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', marginTop: 4, fontSize: 14 }}
            >
              <option value="december_annual">December Annual Promotion (For Jan New Session)</option>
              <option value="june_mid_year">June Mid-Year Promotion (26+ Months / 3+ Years)</option>
            </select>
          </label>

          <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', width: 150 }}>
            Academic Year
            <select
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', marginTop: 4, fontSize: 14 }}
            >
              {getDynamicYears().map(yr => (
                <option key={yr} value={yr}>{yr}</option>
              ))}
            </select>
          </label>
        </div>

        {/* Execution Mode Options (Immediate vs Scheduled) */}
        <div style={{ marginTop: 16, background: '#f8fafc', padding: 14, borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 10 }}>
            Execution Mode (Promotion Scheduling)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 8 }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
              background: executionMode === 'scheduled' ? '#eff6ff' : '#ffffff',
              border: executionMode === 'scheduled' ? '2px solid #3b82f6' : '1px solid #cbd5e1',
              padding: '12px 14px', borderRadius: 10, transition: 'all 0.2s ease',
              boxShadow: executionMode === 'scheduled' ? '0 2px 8px rgba(59, 130, 246, 0.12)' : 'none'
            }}>
              <input
                type="radio"
                name="pageExecutionMode"
                value="scheduled"
                checked={executionMode === 'scheduled'}
                onChange={() => setExecutionMode('scheduled')}
                style={{ width: 18, height: 18, accentColor: '#2563eb', cursor: 'pointer', flexShrink: 0 }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                  📅 Schedule for New Session
                </div>
                <div style={{ fontSize: 11, color: '#475569', marginTop: 2, fontWeight: 500 }}>
                  Effective from {effectiveDate || `${academicYear}-01-01`}
                </div>
              </div>
            </label>

            <label style={{
              display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
              background: executionMode === 'immediate' ? '#f0fdf4' : '#ffffff',
              border: executionMode === 'immediate' ? '2px solid #22c55e' : '1px solid #cbd5e1',
              padding: '12px 14px', borderRadius: 10, transition: 'all 0.2s ease',
              boxShadow: executionMode === 'immediate' ? '0 2px 8px rgba(34, 197, 94, 0.12)' : 'none'
            }}>
              <input
                type="radio"
                name="pageExecutionMode"
                value="immediate"
                checked={executionMode === 'immediate'}
                onChange={() => setExecutionMode('immediate')}
                style={{ width: 18, height: 18, accentColor: '#16a34a', cursor: 'pointer', flexShrink: 0 }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                  ⚡ Promote Immediately Today
                </div>
                <div style={{ fontSize: 11, color: '#475569', marginTop: 2, fontWeight: 500 }}>
                  Class shifts right now
                </div>
              </div>
            </label>
          </div>

          {executionMode === 'scheduled' && (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: '#ffffff', padding: 10, borderRadius: 8, border: '1px solid #cbd5e1' }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
                Effective Start Date:
              </label>
              <input
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
              />
              <span style={{ fontSize: 12, color: '#64748b' }}>
                (Students will remain in current class until this date)
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Scheduled Promotions Summary Banner */}
      {scheduledStudents.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 20, borderLeft: '4px solid #3b82f6', background: '#eff6ff' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1e40af', marginBottom: 8 }}>
            📌 Active Scheduled Promotions ({scheduledStudents.length})
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {scheduledStudents.map(s => (
              <div key={s.id} style={{
                background: '#ffffff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '6px 12px',
                fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}>
                <div>
                  <strong>{s.full_name}</strong> ➔ <span style={{ color: '#2563eb', fontWeight: 600 }}>{s.scheduled_promotion.to_class_name}</span> ({s.scheduled_promotion.effective_date})
                </div>
                <button
                  type="button"
                  onClick={() => handleCancelScheduledPromotion(s.scheduled_promotion.id)}
                  title="Cancel Scheduled Promotion"
                  style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 700, fontSize: 14, padding: 0 }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Promotion Students Table Card */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12
        }}>
          <label style={{ fontSize: 14, fontWeight: 700, color: '#334155', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', margin: 0 }}>
            <input
              type="checkbox"
              checked={selectedStudentIds.length === students.filter(s => s.is_eligible && !s.scheduled_promotion).length && students.filter(s => s.is_eligible && !s.scheduled_promotion).length > 0}
              onChange={(e) => toggleSelectAll(e.target.checked)}
              style={{ margin: 0, width: 16, height: 16, accentColor: '#2563eb', cursor: 'pointer' }}
            />
            <span>Select All Eligible ({students.filter(s => s.is_eligible && !s.scheduled_promotion).length})</span>
          </label>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>
              Selected: <strong style={{ color: '#4f46e5' }}>{selectedStudentIds.length}</strong> student(s)
            </span>
            <button
              type="button"
              disabled={isPromoting || selectedStudentIds.length === 0}
              onClick={handlePromoteSelected}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: selectedStudentIds.length === 0 ? '#cbd5e1' : 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)',
                color: '#ffffff', border: 'none',
                padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 700,
                cursor: selectedStudentIds.length === 0 ? 'not-allowed' : 'pointer',
                boxShadow: selectedStudentIds.length === 0 ? 'none' : '0 4px 12px rgba(79, 70, 229, 0.35)',
                transition: 'all 0.2s ease'
              }}
            >
              <span style={{ fontSize: 16 }}>🎓</span>
              <span>{isPromoting ? 'Processing Promotion...' : `Promote ${selectedStudentIds.length} Selected Student(s)`}</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading students for promotion...</div>
        ) : students.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
            No active students found for promotion under this selection.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%', margin: 0 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'center', width: 40 }}>Select</th>
                  <th>Student Details</th>
                  <th>Branch</th>
                  <th>Current Class</th>
                  <th style={{ textAlign: 'center' }}>Age (Months)</th>
                  <th>Target Next Class</th>
                  <th>Eligibility Status</th>
                  <th style={{ textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {students.map(student => (
                  <tr 
                    key={student.id} 
                    style={{ background: selectedStudentIds.includes(student.id) ? '#f0f9ff' : 'transparent' }}
                  >
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedStudentIds.includes(student.id)}
                        onChange={() => toggleStudentSelect(student.id)}
                      />
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, color: '#1e293b' }}>{student.full_name}</div>
                      <div style={{ fontSize: 12, color: '#94a3b8' }}>ID: {student.student_id}</div>
                    </td>
                    <td>{student.branch_name || '-'}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>
                        {student.current_class_name ? `${student.current_class_name}${student.current_class_section ? ` (${student.current_class_section})` : ''}` : (student.class_grade || 'None')}
                      </div>
                      {student.scheduled_promotion && (
                        <div style={{ fontSize: 11, color: '#2563eb', fontWeight: 600, marginTop: 2 }}>
                          📅 Scheduled: {student.scheduled_promotion.to_class_name} ({student.scheduled_promotion.effective_date})
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>
                      {formatAgeDisplay(student.age_in_months)}
                    </td>
                    <td>
                      <select
                        value={targetClassMap[student.id] || student.recommended_target_class_id || ''}
                        onChange={(e) => setTargetClassMap(prev => ({ ...prev, [student.id]: e.target.value }))}
                        style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, minWidth: 160 }}
                      >
                        <option value="">Select Target Class</option>
                        {(promotionClasses.filter(c => c.branch_id === student.branch_id)).map(c => (
                          <option key={c.id} value={c.id}>
                            {c.name}{c.section ? ` (${c.section})` : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {(() => {
                        const isPromotedThisYear = student.last_completed_promotion && String(student.last_completed_promotion.academic_year) === String(academicYear);
                        if (student.scheduled_promotion) {
                          return (
                            <span style={{
                              padding: '4px 10px', borderRadius: 16, fontSize: 12, fontWeight: 600,
                              background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
                              display: 'inline-flex', alignItems: 'center', gap: 4
                            }}>
                              📅 Scheduled ({student.scheduled_promotion.to_class_name})
                            </span>
                          );
                        }
                        if (isPromotedThisYear) {
                          return (
                            <span style={{
                              padding: '4px 10px', borderRadius: 16, fontSize: 12, fontWeight: 600,
                              background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0',
                              display: 'inline-flex', alignItems: 'center', gap: 4
                            }}>
                              ✅ Promoted ({student.last_completed_promotion.to_class_name})
                            </span>
                          );
                        }
                        if (student.is_eligible) {
                          return (
                            <span style={{
                              padding: '4px 10px', borderRadius: 16, fontSize: 12, fontWeight: 600,
                              background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0',
                              display: 'inline-flex', alignItems: 'center', gap: 4
                            }}>
                              ✨ Ready for Promotion
                            </span>
                          );
                        }
                        return (
                          <span style={{
                            padding: '4px 10px', borderRadius: 16, fontSize: 12, fontWeight: 600,
                            background: '#fffbeb', color: '#b45309', border: '1px solid #fef3c7',
                            display: 'inline-flex', alignItems: 'center', gap: 4
                          }}>
                            ⏳ Not Yet Eligible
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {(() => {
                        const isScheduled = Boolean(student.scheduled_promotion);
                        const isPromotedThisYear = student.last_completed_promotion && String(student.last_completed_promotion.academic_year) === String(academicYear);

                        if (isScheduled) {
                          return (
                            <button
                              type="button"
                              style={{
                                background: '#eff6ff',
                                color: '#1d4ed8',
                                border: '1px solid #93c5fd',
                                padding: '6px 12px',
                                borderRadius: 6,
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: 'pointer',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                transition: 'all 0.15s ease',
                                whiteSpace: 'nowrap'
                              }}
                              onClick={async () => {
                                const sch = student.scheduled_promotion;
                                const confirmReschedule = window.confirm(
                                  `ℹ️ Notice: ${student.full_name} ALREADY HAS A PROMOTION SCHEDULED for "${sch.to_class_name}" on ${sch.effective_date}.\n\nDo you want to update/overwrite this scheduled promotion?`
                                );
                                if (!confirmReschedule) return;

                                const targetId = targetClassMap[student.id] || student.recommended_target_class_id;
                                if (!targetId) {
                                  alert('Please select a target class first');
                                  return;
                                }
                                setIsPromoting(true);
                                try {
                                  const res = await api.post('/students/promotion/promote', {
                                    promotions: [{ student_id: student.id, to_class_id: targetId }],
                                    promotion_type: promotionType,
                                    academic_year: academicYear,
                                    execution_mode: executionMode,
                                    effective_date: executionMode === 'scheduled' ? effectiveDate : null
                                  });
                                  setSuccessMessage(res.data.message || 'Scheduled promotion updated successfully');
                                  fetchPromotionData();
                                } catch (err) {
                                  setError(err.response?.data?.message || 'Promotion failed');
                                } finally {
                                  setIsPromoting(false);
                                }
                              }}
                            >
                              📅 Scheduled (Update)
                            </button>
                          );
                        }

                        if (isPromotedThisYear) {
                          return (
                            <button
                              type="button"
                              style={{
                                background: '#dcfce7',
                                color: '#15803d',
                                border: '1px solid #86efac',
                                padding: '6px 12px',
                                borderRadius: 6,
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: 'pointer',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                transition: 'all 0.15s ease',
                                whiteSpace: 'nowrap'
                              }}
                              onClick={async () => {
                                const targetClassName = student.last_completed_promotion.to_class_name;
                                const confirmRepromote = window.confirm(
                                  `⚠️ Notice: ${student.full_name} is ALREADY PROMOTED to "${targetClassName}" for Academic Session ${academicYear}.\n\nDo you want to re-promote this student to another class?`
                                );
                                if (!confirmRepromote) return;

                                const targetId = targetClassMap[student.id] || student.recommended_target_class_id;
                                if (!targetId) {
                                  alert('Please select a target class first');
                                  return;
                                }
                                setIsPromoting(true);
                                try {
                                  const res = await api.post('/students/promotion/promote', {
                                    promotions: [{ student_id: student.id, to_class_id: targetId }],
                                    promotion_type: promotionType,
                                    academic_year: academicYear,
                                    execution_mode: executionMode,
                                    effective_date: executionMode === 'scheduled' ? effectiveDate : null
                                  });
                                  setSuccessMessage(res.data.message || 'Promoted successfully');
                                  fetchPromotionData();
                                } catch (err) {
                                  setError(err.response?.data?.message || 'Promotion failed');
                                } finally {
                                  setIsPromoting(false);
                                }
                              }}
                            >
                              ✅ Promoted (Re-Promote)
                            </button>
                          );
                        }

                        return (
                          <button
                            type="button"
                            style={{
                              background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
                              color: '#ffffff',
                              border: 'none',
                              padding: '6px 14px',
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer',
                              boxShadow: '0 2px 4px rgba(79, 70, 229, 0.25)',
                              transition: 'all 0.15s ease'
                            }}
                            onClick={async () => {
                              const targetId = targetClassMap[student.id] || student.recommended_target_class_id;
                              if (!targetId) {
                                alert('Please select a target class first');
                                return;
                              }
                              setIsPromoting(true);
                              try {
                                const res = await api.post('/students/promotion/promote', {
                                  promotions: [{ student_id: student.id, to_class_id: targetId }],
                                  promotion_type: promotionType,
                                  academic_year: academicYear,
                                  execution_mode: executionMode,
                                  effective_date: executionMode === 'scheduled' ? effectiveDate : null
                                });
                                setSuccessMessage(res.data.message || 'Promoted successfully');
                                fetchPromotionData();
                              } catch (err) {
                                setError(err.response?.data?.message || 'Promotion failed');
                              } finally {
                                setIsPromoting(false);
                              }
                            }}
                          >
                            Promote Single ➔
                          </button>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
